import { Box, Container, Typography, Button, Grid } from "@mui/material";
import { useDocumentTitle } from "../../utils/data";
import { useNavigate, Link } from "react-router-dom";
import { keyframes } from "@mui/system";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
`;

const floatBall = keyframes`
  0%, 100% { transform: translate(0, 0); }
  25% { transform: translate(20px, -20px); }
  50% { transform: translate(-10px, 20px); }
  75% { transform: translate(10px, -10px); }
`;

const scaleInCenter = keyframes`
  from { opacity: 0; transform: scale(0.85); }
  to { opacity: 1; transform: scale(1); }
`;

const servicesData = [
  {
    title: "AI and Agentic AI Solutions",
    description: "Harness the power of AI and autonomous agents to transform your business operations.",
    emoji: "🤖",
    path: "/services/ai-agentic-solutions",
  },
  {
    title: "IT Consulting & Strategy",
    description: "Strategic guidance to align your technology roadmap with business objectives.",
    emoji: "💡",
    path: "/services/it-consulting-strategy",
  },
  {
    title: "Custom Application Development",
    description: "Bespoke software solutions tailored to your unique business requirements.",
    emoji: "⚙️",
    path: "/services/custom-app-development",
  },
  {
    title: "Quality Engineering & Assurance",
    description: "Rigorous testing and QA processes to ensure peak application performance.",
    emoji: "✅",
    path: "/services/quality-engineering-assurance",
  },
  {
    title: "Mobile Application Development",
    description: "Native and cross-platform mobile apps built with modern technologies.",
    emoji: "📱",
    path: "/services/mobile-app-development",
  },
  {
    title: "Managed IT Services",
    description: "24/7 proactive monitoring and maintenance of your IT infrastructure.",
    emoji: "🔧",
    path: "/services/managed-it-services",
  },
];

const ServicesIndex = () => {
  useDocumentTitle("Briozon - Services");
  const navigate = useNavigate();

  return (
    <>
    {/* Hero Banner */}
    <Box
      sx={{
        background: "linear-gradient(135deg, #f8fafc 0%, #f0f4ff 100%)",
        py: { xs: 6, sm: 8, md: 10 },
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Floating decorative elements */}
      <Box
        sx={{
          position: "absolute",
          top: "10%",
          right: "5%",
          width: 120,
          height: 120,
          borderRadius: "50%",
          background: "linear-gradient(135deg, rgba(91,124,250,0.1) 0%, rgba(118,75,162,0.1) 100%)",
          animation: `${floatBall} 8s ease-in-out infinite`,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "15%",
          left: "3%",
          width: 80,
          height: 80,
          borderRadius: "50%",
          background: "linear-gradient(135deg, rgba(118,75,162,0.1) 0%, rgba(91,124,250,0.1) 100%)",
          animation: `${floatBall} 6s ease-in-out infinite 1s`,
        }}
      />

      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 4, sm: 6, md: 8 }} alignItems="center">
          {/* Left Content */}
          <Grid item xs={12} md={6}>
            <Box>
              <Typography
                variant="overline"
                sx={{
                  fontSize: { xs: "12px", sm: "14px" },
                  fontWeight: 700,
                  color: "#5b7cfa",
                  letterSpacing: "2px",
                  mb: 1,
                  display: "block",
                  animation: `${fadeUp} 0.6s ease-out 0.05s both`,
                }}
              >
                OUR SERVICES
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "32px", sm: "42px", md: "48px" },
                  fontWeight: 800,
                  color: "#1b1f33",
                  lineHeight: 1.2,
                  mb: 2,
                  animation: `${fadeUp} 0.6s ease-out 0.1s both`,
                }}
              >
                Driving Innovation with{" "}
                <Box
                  component="span"
                  sx={{
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  Global Expertise
                </Box>
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: "16px", sm: "19px", md: "20px" },
                  color: "#4c5472",
                  lineHeight: 1.8,
                  mb: 3,
                  animation: `${fadeUp} 0.6s ease-out 0.2s both`,
                }}
              >
                With our expertise and collaborative approach, we drive success, fuel innovation, 
                and foster growth, turning your vision into reality. Together, we navigate challenges 
                and shape the future you aspire to achieve.
              </Typography>
              <Button
                component={Link}
                to="/contact"
                variant="contained"
                sx={{
                  background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                  color: "#fff",
                  fontWeight: 700,
                  px: 4,
                  py: 1.5,
                  borderRadius: "12px",
                  fontSize: "16px",
                  textTransform: "none",
                  boxShadow: "0 8px 24px rgba(91, 124, 250, 0.3)",
                  animation: `${fadeUp} 0.6s ease-out 0.3s both`,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 12px 32px rgba(91, 124, 250, 0.4)",
                  },
                }}
              >
                Get in Touch
              </Button>
            </Box>
          </Grid>

          {/* Right - Creative Visual */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                position: "relative",
                height: { xs: 320, sm: 400, md: 450 },
                animation: `${scaleInCenter} 0.8s ease-out 0.2s both`,
              }}
            >
              {/* Main gradient circle */}
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: { xs: 220, sm: 280, md: 320 },
                  height: { xs: 220, sm: 280, md: 320 },
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, rgba(91,124,250,0.15) 0%, rgba(118,75,162,0.15) 100%)",
                  filter: "blur(40px)",
                }}
              />

              {/* Central icon */}
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: { xs: 100, sm: 120 },
                  height: { xs: 100, sm: 120 },
                  borderRadius: "24px",
                  background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: { xs: "48px", sm: "56px" },
                  boxShadow: "0 20px 40px rgba(91, 124, 250, 0.4)",
                  animation: `${floatBall} 6s ease-in-out infinite`,
                  zIndex: 2,
                }}
              >
                ⚡
              </Box>

              {/* Floating service icons */}
              <Box
                sx={{
                  position: "absolute",
                  top: { xs: "5%", sm: "8%" },
                  left: { xs: "5%", sm: "10%" },
                  width: 56,
                  height: 56,
                  borderRadius: "16px",
                  background: "rgba(255,255,255,0.95)",
                  boxShadow: "0 8px 32px rgba(17, 24, 39, 0.1)",
                  backdropFilter: "blur(10px)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "28px",
                  animation: `${floatBall} 5s ease-in-out infinite 0.5s`,
                  zIndex: 1,
                }}
              >
                🤖
              </Box>

              <Box
                sx={{
                  position: "absolute",
                  top: { xs: "5%", sm: "5%" },
                  right: { xs: "5%", sm: "8%" },
                  width: 56,
                  height: 56,
                  borderRadius: "16px",
                  background: "rgba(255,255,255,0.95)",
                  boxShadow: "0 8px 32px rgba(17, 24, 39, 0.1)",
                  backdropFilter: "blur(10px)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "28px",
                  animation: `${floatBall} 6s ease-in-out infinite 1s`,
                  zIndex: 1,
                }}
              >
                💡
              </Box>

              <Box
                sx={{
                  position: "absolute",
                  bottom: { xs: "8%", sm: "10%" },
                  left: { xs: "8%", sm: "15%" },
                  width: 56,
                  height: 56,
                  borderRadius: "16px",
                  background: "rgba(255,255,255,0.95)",
                  boxShadow: "0 8px 32px rgba(17, 24, 39, 0.1)",
                  backdropFilter: "blur(10px)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "28px",
                  animation: `${floatBall} 7s ease-in-out infinite 1.5s`,
                  zIndex: 1,
                }}
              >
                📱
              </Box>

              <Box
                sx={{
                  position: "absolute",
                  bottom: { xs: "5%", sm: "8%" },
                  right: { xs: "5%", sm: "10%" },
                  width: 56,
                  height: 56,
                  borderRadius: "16px",
                  background: "rgba(255,255,255,0.95)",
                  boxShadow: "0 8px 32px rgba(17, 24, 39, 0.1)",
                  backdropFilter: "blur(10px)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "28px",
                  animation: `${floatBall} 5.5s ease-in-out infinite 2s`,
                  zIndex: 1,
                }}
              >
                ✅
              </Box>

              {/* Additional floating icons */}
              <Box
                sx={{
                  position: "absolute",
                  top: "60%",
                  left: { xs: "15%", sm: "20%" },
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  background: "linear-gradient(135deg, rgba(91,124,250,0.2) 0%, rgba(118,75,162,0.2) 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "20px",
                  animation: `${floatBall} 6s ease-in-out infinite 0.3s`,
                }}
              >
                ⚙️
              </Box>

              <Box
                sx={{
                  position: "absolute",
                  top: "62%",
                  right: { xs: "15%", sm: "18%" },
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  background: "linear-gradient(135deg, rgba(118,75,162,0.2) 0%, rgba(91,124,250,0.2) 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "20px",
                  animation: `${floatBall} 4.5s ease-in-out infinite 1.8s`,
                }}
              >
                🔧
              </Box>

              {/* Decorative rings */}
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: { xs: 200, sm: 260 },
                  height: { xs: 200, sm: 260 },
                  borderRadius: "50%",
                  border: "2px dashed rgba(91, 124, 250, 0.2)",
                  animation: `${floatBall} 10s ease-in-out infinite`,
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: { xs: 280, sm: 360 },
                  height: { xs: 280, sm: 360 },
                  borderRadius: "50%",
                  border: "1px dashed rgba(118, 75, 162, 0.15)",
                  animation: `${floatBall} 12s ease-in-out infinite reverse`,
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>

    {/* Services Grid Section */}
    <Box
      sx={{
        py: { xs: 8, sm: 10, md: 12 },
        background: "#fff",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Dot-grid background */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "radial-gradient(circle, rgba(91,124,250,0.065) 1px, transparent 1px)",
          backgroundSize: "38px 38px",
          pointerEvents: "none",
        }}
      />
      {/* Glow orbs */}
      <Box sx={{ position: "absolute", top: "-10%", right: "-5%", width: 360, height: 360, borderRadius: "50%", background: "radial-gradient(circle, rgba(91,124,250,0.07) 0%, transparent 70%)", pointerEvents: "none" }} />
      <Box sx={{ position: "absolute", bottom: "-10%", left: "-5%", width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle, rgba(118,75,162,0.07) 0%, transparent 70%)", pointerEvents: "none" }} />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: { xs: 6, md: 8 } }}>
          <Typography
            variant="overline"
            sx={{ color: "#5b7cfa", fontWeight: 700, letterSpacing: "3px", display: "block", mb: 2, fontSize: "13px" }}
          >
            WHAT WE DO
          </Typography>
          <Typography
            variant="h3"
            sx={{ fontWeight: 800, fontSize: { xs: "28px", sm: "36px", md: "44px" }, color: "#1b1f33", mb: 3, lineHeight: 1.2 }}
          >
            Our{" "}
            <Box component="span" sx={{ background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
              Services
            </Box>
          </Typography>
          <Typography sx={{ color: "#4c5472", fontSize: { xs: "16px", sm: "18px" }, lineHeight: 1.8, maxWidth: 580, mx: "auto" }}>
            Expert solutions to drive innovation, growth, and operational excellence
          </Typography>
        </Box>

        {/* Service cards — alternating tints + light pastel hover */}
        <Grid container spacing={3}>
          {servicesData.map((service, idx) => {
            const isEven = idx % 2 === 0;
            const defaultBg = isEven
              ? "linear-gradient(160deg, #f0f4ff 0%, #f8faff 100%)"
              : "linear-gradient(160deg, #f7f2ff 0%, #fdf8ff 100%)";
            const defaultBorder = isEven
              ? "1px solid rgba(91,124,250,0.14)"
              : "1px solid rgba(118,75,162,0.14)";
            const hoverBg = isEven
              ? "linear-gradient(135deg, #dce7ff 0%, #eadcff 100%)"
              : "linear-gradient(135deg, #e5d8ff 0%, #d8e7ff 100%)";
            const accentColor = isEven ? "#5b7cfa" : "#764ba2";
            return (
              <Grid item xs={12} sm={6} md={4} key={service.title} sx={{ display: "flex", flexDirection: "column" }}>
                <Box
                  onClick={() => navigate(service.path)}
                  sx={{
                    flexGrow: 1,
                    p: { xs: 3, sm: 4 },
                    borderRadius: "24px",
                    background: defaultBg,
                    border: defaultBorder,
                    boxShadow: "0 4px 20px rgba(91,124,250,0.06)",
                    position: "relative",
                    overflow: "hidden",
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      background: hoverBg,
                      transform: "translateY(-9px)",
                      boxShadow: isEven
                        ? "0 24px 60px rgba(91,124,250,0.2)"
                        : "0 24px 60px rgba(118,75,162,0.2)",
                      border: isEven
                        ? "1px solid rgba(91,124,250,0.3)"
                        : "1px solid rgba(118,75,162,0.3)",
                      "& .svc-num": {
                        color: isEven ? "rgba(91,124,250,0.14)" : "rgba(118,75,162,0.14)",
                      },
                      "& .svc-icon": {
                        background: isEven
                          ? "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)"
                          : "linear-gradient(135deg, #764ba2 0%, #5b7cfa 100%)",
                        boxShadow: `0 10px 28px ${isEven ? "rgba(91,124,250,0.4)" : "rgba(118,75,162,0.4)"}`,
                      },
                      "& .svc-badge": {
                        background: accentColor,
                        color: "#fff",
                        border: `1px solid ${accentColor}`,
                      },
                    },
                  }}
                >
                  {/* Ghost number */}
                  <Typography
                    className="svc-num"
                    sx={{
                      position: "absolute",
                      top: -12,
                      right: 14,
                      fontSize: "88px",
                      fontWeight: 900,
                      color: isEven ? "rgba(91,124,250,0.08)" : "rgba(118,75,162,0.08)",
                      lineHeight: 1,
                      userSelect: "none",
                      letterSpacing: "-5px",
                      transition: "color 0.4s ease",
                      pointerEvents: "none",
                    }}
                  >
                    {String(idx + 1).padStart(2, "0")}
                  </Typography>

                  {/* Icon */}
                  <Box
                    className="svc-icon"
                    sx={{
                      width: 66,
                      height: 66,
                      borderRadius: "18px",
                      background: isEven
                        ? "linear-gradient(135deg, rgba(91,124,250,0.15) 0%, rgba(118,75,162,0.15) 100%)"
                        : "linear-gradient(135deg, rgba(118,75,162,0.15) 0%, rgba(91,124,250,0.15) 100%)",
                      border: `1px solid ${isEven ? "rgba(91,124,250,0.2)" : "rgba(118,75,162,0.2)"}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "32px",
                      mb: 3,
                      transition: "all 0.4s ease",
                    }}
                  >
                    {service.emoji}
                  </Box>

                  {/* Title */}
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: "18px", sm: "20px" },
                      color: "#1b1f33",
                      mb: 1.5,
                      lineHeight: 1.3,
                    }}
                  >
                    {service.title}
                  </Typography>

                  {/* Description */}
                  <Typography
                    sx={{
                      color: "#4c5472",
                      fontSize: "14px",
                      lineHeight: 1.85,
                      flexGrow: 1,
                      mb: 3,
                    }}
                  >
                    {service.description}
                  </Typography>

                  {/* Learn More badge */}
                  <Box
                    className="svc-badge"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.75,
                      px: 2.5,
                      py: 1,
                      borderRadius: "50px",
                      border: `1px solid ${isEven ? "rgba(91,124,250,0.3)" : "rgba(118,75,162,0.3)"}`,
                      color: accentColor,
                      fontWeight: 700,
                      fontSize: "13px",
                      alignSelf: "flex-start",
                      transition: "all 0.4s ease",
                    }}
                  >
                    Learn More →
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>

    {/* Preparing for Your Success Section */}
    <Box sx={{ py: { xs: 8, sm: 10, md: 12 }, background: "#fff" }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: "center", mb: { xs: 5, sm: 6 } }}>
          <Typography
            variant="overline"
            sx={{
              fontSize: { xs: "12px", sm: "14px" },
              fontWeight: 700,
              color: "#5b7cfa",
              letterSpacing: "2px",
              mb: 1,
              display: "block",
              animation: `${fadeUp} 0.6s ease-out both`,
            }}
          >
            PREPARING FOR YOUR SUCCESS
          </Typography>
          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: "28px", sm: "36px", md: "42px" },
              fontWeight: 800,
              color: "#1b1f33",
              animation: `${fadeUp} 0.6s ease-out 0.05s both`,
            }}
          >
            Trusted{" "}
            <Box
              component="span"
              sx={{
                background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Source
            </Box>
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4, 1fr)" },
            gap: { xs: 3, sm: 4 },
          }}
        >
          {[
            {
              icon: "🏆",
              title: "10+ Years' Experience",
              description: "10+ Years' experience in providing Design Consulting, Technical Advisory, and Web Development Services.",
              delay: 0,
            },
            {
              icon: "👨‍💻",
              title: "Strong Team",
              description: "A strong team of core Web Technologies and UX/UI experts, supported by a specialized Quality Assurance team.",
              delay: 0.1,
            },
            {
              icon: "✨",
              title: "Quality Deliverables",
              description: "Our multi-skilled team of experts works collaboratively to ensure quality deliverables to meet and exceed client expectations.",
              delay: 0.2,
            },
            {
              icon: "🤝",
              title: "End-to-end Partner",
              description: "Your end-to-end partner for the application development, support and enhancements.",
              delay: 0.3,
            },
          ].map((item) => (
            <Box
              key={item.title}
              sx={{
                p: { xs: 3, sm: 4 },
                background: "linear-gradient(135deg, #f6f8ff 0%, #eef3ff 100%)",
                borderRadius: "20px",
                border: "1px solid rgba(91, 124, 250, 0.15)",
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                animation: `${fadeUp} 0.6s ease-out ${item.delay}s both`,
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "4px",
                  background: "linear-gradient(90deg, #5b7cfa 0%, #764ba2 100%)",
                  transform: "scaleX(0)",
                  transformOrigin: "left",
                  transition: "transform 0.4s ease",
                },
                "&:hover": {
                  transform: "translateY(-8px)",
                  boxShadow: "0 20px 48px rgba(91, 124, 250, 0.15)",
                  border: "1px solid rgba(91, 124, 250, 0.3)",
                  "&::before": {
                    transform: "scaleX(1)",
                  },
                  "& .success-icon": {
                    transform: "scale(1.1) rotate(5deg)",
                    background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                  },
                },
              }}
            >
              <Box
                className="success-icon"
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "16px",
                  background: "linear-gradient(135deg, rgba(91,124,250,0.12) 0%, rgba(118,75,162,0.12) 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "32px",
                  mb: 2.5,
                  transition: "all 0.4s ease",
                }}
              >
                {item.icon}
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: "20px", sm: "22px" },
                  fontWeight: 700,
                  color: "#1f2937",
                  mb: 1.5,
                }}
              >
                {item.title}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "16px", sm: "17px" },
                  color: "#4b5563",
                  lineHeight: 1.7,
                }}
              >
                {item.description}
              </Typography>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>

    {/* Get in Touch CTA Section */}
    <Box
      sx={{
        py: { xs: 8, sm: 10, md: 12 },
        background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Animated background elements */}
      <Box
        sx={{
          position: "absolute",
          top: "10%",
          left: "5%",
          width: 120,
          height: 120,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.08)",
          animation: `${floatBall} 8s ease-in-out infinite`,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "15%",
          right: "8%",
          width: 180,
          height: 180,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.06)",
          animation: `${floatBall} 10s ease-in-out infinite 1s`,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 400,
          height: 400,
          borderRadius: "50%",
          border: "1px dashed rgba(255,255,255,0.15)",
          animation: `${keyframes`
            from { transform: translate(-50%, -50%) rotate(0deg); }
            to { transform: translate(-50%, -50%) rotate(360deg); }
          `} 40s linear infinite`,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 550,
          height: 550,
          borderRadius: "50%",
          border: "1px dashed rgba(255,255,255,0.1)",
          animation: `${keyframes`
            from { transform: translate(-50%, -50%) rotate(0deg); }
            to { transform: translate(-50%, -50%) rotate(-360deg); }
          `} 50s linear infinite`,
        }}
      />

      <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
        <Box sx={{ textAlign: "center" }}>
          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: "26px", sm: "34px", md: "42px" },
              fontWeight: 800,
              color: "#fff",
              mb: 3,
              animation: `${fadeUp} 0.6s ease-out both`,
              textShadow: "0 2px 20px rgba(0,0,0,0.15)",
            }}
          >
            Get in Touch With Us
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: "16px", sm: "19px", md: "20px" },
              color: "rgba(255,255,255,0.9)",
              lineHeight: 1.9,
              maxWidth: "700px",
              mx: "auto",
              textAlign: "justify",
              animation: `${fadeUp} 0.6s ease-out 0.1s both`,
            }}
          >
            Reach out to us today to learn how we can drive transformation and help your 
            business thrive in an ever-changing digital landscape.
          </Typography>

          <Box
            sx={{
              mt: 5,
              display: "flex",
              gap: 2,
              justifyContent: "center",
              flexWrap: "wrap",
              animation: `${fadeUp} 0.6s ease-out 0.2s both`,
            }}
          >
            <Box
              component="button"
              onClick={() => navigate("/contact")}
              sx={{
                px: 4,
                py: 1.5,
                background: "#fff",
                color: "#5b7cfa",
                borderRadius: "50px",
                fontSize: { xs: "16px", sm: "17px" },
                fontWeight: 700,
                cursor: "pointer",
                border: "none",
                transition: "all 0.3s ease",
                boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                "&:hover": {
                  transform: "translateY(-3px)",
                  boxShadow: "0 12px 32px rgba(0,0,0,0.2)",
                },
              }}
            >
              Let's Connect
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
    </>
  );
};

export default ServicesIndex;
